import { ChartPieIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  FlexList,
  FlexListItem,
  InfoCircledIcon,
  Percentage,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import { AssetAmount } from "@sundaeswap/asset";
import cx from "classnames";
import { FC } from "react";
import { ADA_METADATA } from "../../../constants/cardano.constants";
import { Asset } from "../../../gql/generated/stats2.sdk";
import { useCalculateContributionOutcome } from "../../../hooks/taste-test/useCalculateContributionOutcome";
import { useCurrentTasteTestContribution } from "../../../hooks/taste-test/useCurrentTasteTestContribution";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useProfileAppContext } from "../../../stores";
import { IAssetMetaData } from "../../../types/Asset.types";
import { getAssetName } from "../../../utils/assets.utils";
import AssetWithAssetName from "../../AssetWithAssetName";
import BalanceWithAssetName from "../../BalanceWithAssetName";

interface ITasteTestCurrentPositionDetailsProps {
  isClaiming?: boolean;
}

const TasteTestCurrentPositionDetails: FC<ITasteTestCurrentPositionDetailsProps> = ({
  isClaiming,
}) => {
  const { t, currentLocale } = useI18N("taste-test");
  const {
    derived: { latestHistoryRecord },
    state: { tasteTest },
  } = useTasteTestContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const currentContribution = useCurrentTasteTestContribution();
  const suppliedAsset = tasteTest?.asset ?? ({} as IAssetMetaData);
  const { calculateContributionOutcome, calculateShareOfPool } = useCalculateContributionOutcome({
    latestHistoryRecord,
    currentBalance: tasteTest?.currentBalance,
  });
  const { contributionValueAdaAssetAmount, contributionValueTokenAssetAmount } =
    calculateContributionOutcome();

  if (!latestHistoryRecord) return null;

  const { minUTXOCumulative, foldFeeCumulative } = latestHistoryRecord;

  const items = [minUTXOCumulative, foldFeeCumulative].map((value, i) => ({
    id: i,
    label: (
      <Text className="flex items-center gap-1" size="sm" tag="span" variant="muted">
        {t(`detail.widget.${i === 0 ? "refundableDeposit" : "processorFee"}`)}
        <Tooltip
          tooltipLabel={t(`detail.widget.${i === 0 ? "refundableDeposit" : "processorFee"}`, {
            context: "tooltip",
          })}
          trigger={
            <div>
              <InfoCircledIcon className={cx("h-4 w-4 cursor-help", toolkitCx.layout.text.muted)} />
            </div>
          }
        />
      </Text>
    ),
    value: (
      <BalanceWithAssetName
        abbreviate={false}
        asset={ADA_METADATA}
        locale={currentLocale}
        showSymbol
        size="sm"
        value={new AssetAmount(value[0].amount, ADA_METADATA)}
      />
    ),
  }));

  return (
    <div
      className={cx({
        [`${toolkitCx.layout.spacing.horizontal.md} ${toolkitCx.layout.spacing.bottom.md}`]:
          !isClaiming,
      })}
    >
      <FlexList>
        <FlexListItem
          field={{
            label: t("pool", { ns: "glossary" }),
            variant: "muted",
          }}
          value={{
            label: (
              <AssetWithAssetName
                assetName={`${getAssetName(ADA_METADATA)}-${getAssetName(suppliedAsset)}`}
                assets={[ADA_METADATA, suppliedAsset as Asset]}
              />
            ),
          }}
        />

        <FlexListItem
          field={{
            label: isClaiming
              ? t("detail.widget.currentContribution") // "Your Contribution"
              : t("detail.widget.currentDeposit"), // "Current Contribution"
            variant: "muted",
          }}
          value={{
            label: (
              <BalanceWithAssetName
                abbreviate={false}
                asset={ADA_METADATA}
                locale={currentLocale}
                showSymbol
                size="sm"
                value={currentContribution}
              />
            ),
            variant: "muted",
          }}
        />

        {
          // we already render the estimated value while claiming and don't need to render it twice
          !isClaiming && (
            <FlexListItem
              field={{
                label: t("detail.txOverview.lpTokensValue"),
                variant: "muted",
              }}
              value={{
                label: (
                  <Text tag="div" className="inline-flex gap-1" size="sm">
                    <Balance
                      abbreviate={false}
                      size="sm"
                      locale={currentLocale}
                      privacy={privacyEnabled}
                      value={contributionValueAdaAssetAmount}
                    />{" "}
                    /{" "}
                    <BalanceWithAssetName
                      abbreviate={false}
                      className="inline-block"
                      asset={
                        contributionValueTokenAssetAmount.metadata as unknown as IAssetMetaData
                      }
                      size="sm"
                      showSymbol={false}
                      locale={currentLocale}
                      privacy={privacyEnabled}
                      value={contributionValueTokenAssetAmount}
                    />
                  </Text>
                ),
              }}
            />
          )
        }

        <FlexListItem
          field={{
            label: (
              <Text className="inline-block" tag="span" variant="muted" size="sm">
                {t("share", { ns: "common", context: "poolEst" })}
                <ChartPieIcon className="ml-1 inline h-4 w-4 align-sub" />
              </Text>
            ),
            variant: "muted",
          }}
          value={{
            label: <Percentage locale={currentLocale} value={calculateShareOfPool()} size="sm" />,
          }}
        />

        {!isClaiming &&
          items.map(({ id, label, value }) => (
            <FlexListItem
              key={id}
              field={{ label, variant: "muted" }}
              value={{
                label: value,
              }}
            />
          ))}
      </FlexList>
    </div>
  );
};

export default TasteTestCurrentPositionDetails;
